import { apiClient } from "./auth";
import type { PipelineStagesResponse } from "../Interfaces/PipelineStagesInterface";
import { normalizeOpportunityType } from "../constants/opportunityTypes";
import { formatAbbreviationForDisplay } from "../utils/displayFormatters";
import { getMastersFilterOptionsApi } from "./mastersFilterOptions";
import {
  CHANNEL_OPTIONS,
  mapSourceChannelsToFilterOptions,
  type LeadSourceFilterOption,
} from "../constants/leadSourceOptions";

/** Outbound Call Form — RNR/DND (matches tenant-config domain) */
export interface OutboundConnectivityRemarkOption {
  id: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
}

export interface OutboundConnectivityAttemptSettings {
  maxAttempts: number;
  retryIntervalsMinutes: number[];
}

export interface OutboundConnectivityStatusFormConfig {
  attempt: OutboundConnectivityAttemptSettings;
  connectivityRemarks: OutboundConnectivityRemarkOption[];
}

export interface TenantConfigResponse {
  success: boolean;
  data?: {
    teamTypes?: string[];
    rnrConfig?: TenantRnrConfig;
    [key: string]: unknown;
  };
}

export interface TenantRnrConfig {
  timeoutHours: number;
  enabled: boolean;
  addToPastDue: boolean;
  rnrMaxAttempts: number;
  /** v6.0 — per-attempt RNR comeback intervals (hours); length should match rnrMaxAttempts when set */
  retryIntervalsHours?: number[];
  dndRetryIntervalHours: number;
  dndMaxAttempts: number;
  dndBucketExpiryDays?: number;
}

/** Response from GET/PUT /admin/tenant-config/rnr-config/attempts */
export interface RnrAttemptConfig {
  maxAttempts: number;
  retryIntervalsHours: number[];
  enabled: boolean;
}

export interface RnrAttemptConfigResponse {
  success: boolean;
  message?: string;
  data: RnrAttemptConfig;
}

/** GET/PUT /admin/tenant-config/opportunity-expiry */
export interface OpportunityExpiryConfig {
  global: number;
  byType: Record<string, number>;
}

export interface OpportunityExpiryConfigResponse {
  success: boolean;
  message?: string;
  data: OpportunityExpiryConfig;
}

export interface UpdateOpportunityExpiryPayload {
  global?: number;
  byType?: Record<string, number>;
}

export interface UpdateTenantRnrConfigPayload {
  rnrConfig: Partial<TenantRnrConfig>;
}

/**
 * Fetch tenant configuration (includes teamTypes for Team Management).
 * GET /admin/tenant-config
 */
export async function getTenantConfigApi(): Promise<TenantConfigResponse> {
  const response = await apiClient.get<TenantConfigResponse>(
    "/admin/tenant-config"
  );
  return response.data;
}

export async function patchTenantConfigApi(
  body: Record<string, unknown>
): Promise<TenantConfigResponse> {
  const response = await apiClient.patch<
    Record<string, unknown>,
    TenantConfigResponse
  >("/admin/tenant-config", body);
  return response.data;
}

export interface OutboundConnectivitySettingsData {
  rnr: OutboundConnectivityStatusFormConfig;
  dnd: OutboundConnectivityStatusFormConfig;
}

export interface OutboundConnectivitySettingsResponse {
  success: boolean;
  data?: OutboundConnectivitySettingsData;
  message?: string;
}

/**
 * RNR/DND attempt + remark options for Outbound Call Form.
 * GET — requires OUTBOUND_CALLING VIEW_DETAILS (same as pipeline stages).
 */
export async function getOutboundConnectivitySettingsApi(): Promise<OutboundConnectivitySettingsResponse> {
  const response = await apiClient.get<OutboundConnectivitySettingsResponse>(
    "/admin/tenant-config/outbound-connectivity/settings"
  );
  return response.data;
}

/**
 * Update only RNR/DND configuration for the tenant.
 * PATCH /admin/tenant-config
 */
export async function updateTenantRnrConfigApi(
  rnrConfig: Partial<TenantRnrConfig>
): Promise<TenantConfigResponse> {
  const response = await apiClient.patch<
    UpdateTenantRnrConfigPayload,
    TenantConfigResponse
  >("/admin/tenant-config", { rnrConfig });
  return response.data;
}

/**
 * RNR attempt limits + per-attempt retry hours (v6.0 dedicated admin endpoints).
 * GET /admin/tenant-config/rnr-config/attempts
 */
export async function getRnrAttemptConfigApi(): Promise<RnrAttemptConfigResponse> {
  const response = await apiClient.get<RnrAttemptConfigResponse>(
    "/admin/tenant-config/rnr-config/attempts"
  );
  return response.data;
}

/**
 * PUT /admin/tenant-config/rnr-config/attempts
 * Body: { maxAttempts?, retryIntervalsHours?, enabled? } — retryIntervalsHours.length must equal maxAttempts when both sent.
 */
export async function updateRnrAttemptConfigApi(
  payload: Partial<
    Pick<RnrAttemptConfig, "maxAttempts" | "retryIntervalsHours" | "enabled">
  >
): Promise<RnrAttemptConfigResponse> {
  const response = await apiClient.put<
    Partial<
      Pick<RnrAttemptConfig, "maxAttempts" | "retryIntervalsHours" | "enabled">
    >,
    RnrAttemptConfigResponse
  >("/admin/tenant-config/rnr-config/attempts", payload);
  return response.data;
}

/**
 * GET /admin/tenant-config/opportunity-expiry
 */
export async function getOpportunityExpiryApi(): Promise<OpportunityExpiryConfigResponse> {
  const response = await apiClient.get<OpportunityExpiryConfigResponse>(
    "/admin/tenant-config/opportunity-expiry"
  );
  return response.data;
}

/**
 * PUT /admin/tenant-config/opportunity-expiry
 */
export async function updateOpportunityExpiryApi(
  payload: UpdateOpportunityExpiryPayload
): Promise<OpportunityExpiryConfigResponse> {
  const response = await apiClient.put<
    UpdateOpportunityExpiryPayload,
    OpportunityExpiryConfigResponse
  >("/admin/tenant-config/opportunity-expiry", payload);
  return response.data;
}

/**
 * Get team types from tenant config. Returns empty array if not available.
 */
export async function getTeamTypesApi(): Promise<string[]> {
  const res = await getTenantConfigApi();
  const types = res.data?.teamTypes;
  return Array.isArray(types) ? types : [];
}

/** Lead source option from GET form-options (Add New Contact dropdown). */
export interface ContactFormLeadSource {
  code: string;
  name: string;
  displayOrder?: number;
}

/** Campaign option from GET form-options (Add New Contact dropdown). */
export interface ContactFormCampaign {
  code: string;
  name: string;
  campaignType?: string;
}

export interface ContactFormOptionsResponse {
  success: boolean;
  data?: {
    leadSources?: ContactFormLeadSource[];
    campaigns?: ContactFormCampaign[];
    sourceChannels?: unknown[];
    campaignGroupings?: unknown[];
  };
}

/**
 * Fetch form options for Add New Contact (lead sources, campaigns, etc.).
 * GET /admin/tenant-config/campaign-groupings/form-options
 */
export async function getContactFormOptionsApi(): Promise<ContactFormOptionsResponse> {
  const response = await apiClient.get<ContactFormOptionsResponse>(
    "/admin/tenant-config/campaign-groupings/form-options"
  );
  const data = response.data?.data;
  if (!data) return response.data;

  const leadSources = data.leadSources?.map((source) => ({
    ...source,
    name: formatAbbreviationForDisplay(source.name || source.code),
  }));
  const sourceChannels = (
    data.sourceChannels as ContactFormLeadSource[] | undefined
  )?.map((channel) => ({
    ...channel,
    name: formatAbbreviationForDisplay(channel.name || channel.code),
  }));

  return {
    ...response.data,
    data: {
      ...data,
      ...(leadSources ? { leadSources } : {}),
      ...(sourceChannels ? { sourceChannels } : {}),
    },
  };
}

export interface CampaignGroupingMapping {
  rawValue: string;
  groupName: string;
  isActive?: boolean;
}

export interface CampaignGroupingConfig {
  props?: {
    category?: string;
    mappings?: CampaignGroupingMapping[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface CampaignGroupingResponse {
  success: boolean;
  data?: CampaignGroupingConfig;
}

export async function getSourceGroupingConfigApi(): Promise<CampaignGroupingResponse> {
  const response = await apiClient.get<CampaignGroupingResponse>(
    "/admin/tenant-config/campaign-groupings/source"
  );
  return response.data;
}

/**
 * Agent-accessible source campaign grouping lookup (read-only).
 * GET /admin/tenant-config/campaign-groupings/source/lookup
 */
export async function getSourceGroupingConfigLookupApi(): Promise<CampaignGroupingResponse> {
  const response = await apiClient.get<CampaignGroupingResponse>(
    "/admin/tenant-config/campaign-groupings/source/lookup"
  );
  return response.data;
}

export async function getCampaignGroupingConfigApi(): Promise<CampaignGroupingResponse> {
  const response = await apiClient.get<CampaignGroupingResponse>(
    "/admin/tenant-config/campaign-groupings/campaign"
  );
  return response.data;
}

export interface SourceChannelOption {
  code: string;
  name: string;
  displayOrder?: number;
}

export interface SourceChannelsResponse {
  success: boolean;
  data?: SourceChannelOption[];
}

function toSourceChannelOptions(
  options: LeadSourceFilterOption[]
): SourceChannelOption[] {
  return options.map((opt, index) => ({
    code: opt.id,
    name: opt.label,
    displayOrder: index,
  }));
}

/** Same Channel list as My Leads / Overview filters (`/admin/masters/filter-options`). */
export async function getSourceChannelsApi(): Promise<SourceChannelsResponse> {
  try {
    const response = await getMastersFilterOptionsApi({ isActive: true });
    const merged = mapSourceChannelsToFilterOptions(
      response.data?.sourceChannels
    );
    return {
      success: true,
      data: toSourceChannelOptions(merged),
    };
  } catch {
    return {
      success: true,
      data: toSourceChannelOptions(CHANNEL_OPTIONS),
    };
  }
}

export type PipelineStageStatus = "OPEN" | "WON" | "LOST" | "AGED_OUT";

/**
 * Pipeline stages API opportunity types in the path.
 * Must match the type from GET /opportunities/patient/:id/list (and disposition config):
 * use the same value for pipeline-stages so LAB calls .../LAB/stages, not .../DIAGNOSTICS/stages.
 */
export const PIPELINE_STAGE_OPPORTUNITY_TYPES = [
  "ADMISSION",
  "LAB",
  "LAB_TEST",
  "RADIOLOGY",
  "DIAGNOSTICS",
  "PHARMACY",
  "PROCEDURE",
  "PACKAGE",
  "FOLLOWUP",
  "DR_CONSULTATION",
  "PHYSIOTHERAPY",
] as const;

export type PipelineStageOpportunityType =
  (typeof PIPELINE_STAGE_OPPORTUNITY_TYPES)[number];

/**
 * Map opportunity type (from opportunities/patient/:id/list or display) to the path segment
 * for pipeline-stages API. Uses the same type as disposition (e.g. LAB stays LAB, not DIAGNOSTICS).
 * Uses normalizeOpportunityType for aliases (e.g. CONSULTATION → DR_CONSULTATION, FOLLOW_UP → FOLLOWUP).
 */
function toPipelineStageOpportunityType(
  input: string
): PipelineStageOpportunityType {
  const canonical = normalizeOpportunityType(input);
  const passthrough: readonly string[] = PIPELINE_STAGE_OPPORTUNITY_TYPES;
  if (canonical && passthrough.includes(canonical)) {
    return canonical as PipelineStageOpportunityType;
  }
  const n = input.trim().toUpperCase().replace(/\s+/g, "_");
  if (passthrough.includes(n)) return n as PipelineStageOpportunityType;
  const mapping: Record<string, PipelineStageOpportunityType> = {
    CONSULTATION: "DR_CONSULTATION",
    FOLLOW_UP: "FOLLOWUP",
  };
  if (mapping[n]) return mapping[n];
  // Tenant-configured types (e.g. OPD_PROCEDURE) must not collapse to ADMISSION
  // or an aliased built-in — pass the raw key through to the stages API.
  if (n) return n as PipelineStageOpportunityType;
  return "ADMISSION";
}

/**
 * Fetch pipeline stages for an opportunity type and status.
 * GET /admin/tenant-config/pipeline-stages/:opportunityType/stages?status=OPEN|WON|LOST|AGED_OUT
 * Path segment :opportunityType uses the same type as from opportunities/patient/:id/list (e.g. LAB, RADIOLOGY, DIAGNOSTICS, PHARMACY, DR_CONSULTATION, FOLLOWUP, ADMISSION, PROCEDURE, PACKAGE).
 * Used by OutboundCallForm to populate the Stage dropdown.
 */
export async function getPipelineStagesForStatusApi(
  opportunityType: string,
  status: PipelineStageStatus = "OPEN"
): Promise<PipelineStagesResponse> {
  const pathSegment = toPipelineStageOpportunityType(opportunityType);
  const encodedType = encodeURIComponent(pathSegment);
  const response = await apiClient.get<PipelineStagesResponse>(
    `/admin/tenant-config/pipeline-stages/${encodedType}/stages`,
    { params: { status } }
  );
  return response.data;
}

/**
 * Agent-accessible pipeline stages for a status (read-only).
 * GET /admin/tenant-config/pipeline-stages/:opportunityType/stages/lookup?status=
 */
export async function getPipelineStagesForStatusLookupApi(
  opportunityType: string,
  status: PipelineStageStatus = "OPEN"
): Promise<PipelineStagesResponse> {
  const pathSegment = toPipelineStageOpportunityType(opportunityType);
  const encodedType = encodeURIComponent(pathSegment);
  const response = await apiClient.get<PipelineStagesResponse>(
    `/admin/tenant-config/pipeline-stages/${encodedType}/stages/lookup`,
    { params: { status } }
  );
  return response.data;
}

export interface PipelineStagesByOpportunityTypeResponse {
  success: boolean;
  data?: {
    opportunityType?: string;
    stages?: unknown[];
    openStages?: unknown[];
    lostStages?: unknown[];
    wonStages?: unknown[];
    byStatus?: Record<string, unknown[]>;
    props?: {
      defaultStageId?: string | null;
      defaultLostStageId?: string | null;
      defaultAgedOutStageId?: string | null;
      stages?: unknown[];
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
}

/**
 * Fetch pipeline stages for an opportunity type.
 * GET /admin/tenant-config/pipeline-stages/:opportunityType
 */
export async function getPipelineStagesByOpportunityTypeApi(
  opportunityType: string
): Promise<PipelineStagesByOpportunityTypeResponse> {
  const pathSegment = toPipelineStageOpportunityType(opportunityType);
  const encodedType = encodeURIComponent(pathSegment);
  const response = await apiClient.get<PipelineStagesByOpportunityTypeResponse>(
    `/admin/tenant-config/pipeline-stages/${encodedType}`
  );
  return response.data;
}

/**
 * Agent-accessible pipeline stage config for one opportunity type (read-only).
 * GET /admin/tenant-config/pipeline-stages/:opportunityType/lookup
 */
export async function getPipelineStagesByOpportunityTypeLookupApi(
  opportunityType: string
): Promise<PipelineStagesByOpportunityTypeResponse> {
  const pathSegment = toPipelineStageOpportunityType(opportunityType);
  const encodedType = encodeURIComponent(pathSegment);
  const response = await apiClient.get<PipelineStagesByOpportunityTypeResponse>(
    `/admin/tenant-config/pipeline-stages/${encodedType}/lookup`
  );
  return response.data;
}

export interface CreatePipelineStageConfigStage {
  stageId: string;
  name: string;
  order: number;
  description?: string;
  validStatuses: Array<"OPEN" | "WON" | "LOST" | "AGED_OUT">;
  isActive: boolean;
  color?: string;
  icon?: string;
}

export interface CreatePipelineStageConfigPayload {
  opportunityType: string;
  wonStageId: string;
  lostStageIds: string[];
  defaultStageId?: string | null;
  defaultLostStageId?: string | null;
  defaultAgedOutStageId?: string | null;
  allowBackwardMovement: boolean;
  stages: CreatePipelineStageConfigStage[];
}

export interface UpdatePipelineStageConfigPayload {
  allowBackwardMovement?: boolean;
  stages?: CreatePipelineStageConfigStage[];
  wonStageId?: string;
  lostStageIds?: string[];
  defaultStageId?: string | null;
  defaultLostStageId?: string | null;
  defaultAgedOutStageId?: string | null;
}

export interface PipelineStageConfigListItem {
  id?: string;
  _id?: string;
  opportunityType: string;
  defaultStageId?: string;
  stages?: Array<{
    stageId: string;
    name: string;
    order: number;
    validStatuses: Array<"OPEN" | "WON" | "LOST" | "AGED_OUT">;
    isActive?: boolean;
  }>;
}

export async function createPipelineStageConfigApi(
  payload: CreatePipelineStageConfigPayload
): Promise<{ success: boolean; message?: string; data?: unknown }> {
  const response = await apiClient.post<
    CreatePipelineStageConfigPayload,
    { success: boolean; message?: string; data?: unknown }
  >("/admin/tenant-config/pipeline-stages", payload);
  return response.data;
}

export async function updatePipelineStageConfigApi(
  opportunityType: string,
  payload: UpdatePipelineStageConfigPayload
): Promise<{ success: boolean; message?: string; data?: unknown }> {
  const pathSegment = toPipelineStageOpportunityType(opportunityType);
  const encodedType = encodeURIComponent(pathSegment);
  const response = await apiClient.put<
    UpdatePipelineStageConfigPayload,
    { success: boolean; message?: string; data?: unknown }
  >(`/admin/tenant-config/pipeline-stages/${encodedType}`, payload);
  return response.data;
}

export async function listPipelineStageConfigsApi(): Promise<{
  success: boolean;
  data: PipelineStageConfigListItem[];
  total?: number;
}> {
  const response = await apiClient.get<{
    success: boolean;
    data: PipelineStageConfigListItem[];
    total?: number;
  }>("/admin/tenant-config/pipeline-stages");
  return response.data;
}

/** Pipeline stage config row from GET .../pipeline-stages/lookup. */
export interface PipelineStageConfigLookupItem {
  id?: string;
  _id?: string;
  opportunityType: string;
  stages?: Array<{
    stageId: string;
    name: string;
    order: number;
    validStatuses: Array<"OPEN" | "WON" | "LOST" | "AGED_OUT">;
    isActive?: boolean;
  }>;
  wonStageId?: string;
  lostStageIds?: string[];
  allowBackwardMovement?: boolean;
}

/**
 * Agent-accessible list of all pipeline stage configs (read-only).
 * GET /admin/tenant-config/pipeline-stages/lookup
 */
export async function listPipelineStageConfigsLookupApi(): Promise<{
  success: boolean;
  data: PipelineStageConfigLookupItem[];
  total?: number;
}> {
  const response = await apiClient.get<{
    success: boolean;
    data: PipelineStageConfigLookupItem[];
    total?: number;
  }>("/admin/tenant-config/pipeline-stages/lookup");
  return response.data;
}
